import { StatusActiveIcon } from '@tableau/icons/StatusActiveIcon';
import { StatusErrorIcon } from '@tableau/icons/StatusErrorIcon';
import { StatusInProgressIcon } from '@tableau/icons/StatusInProgressIcon';
import { ButtonType, ButtonWidget } from '@tableau/widgets';
import { ArrayRowStore, BodyCell, DataGrid, HeaderColumn } from '@tableau/widgets-datagrid';
import React from 'react';

import { L10n } from '~commonInfra/L10n/L10n';
import { Messages } from '~src/codegen/Localize';
import { Duration } from '~src/flow/utils/Duration';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';
import { classes } from '~src/style';
import { TaskDetailsSpaceStyles } from '~src/tasks/components/taskDetailsSpace/TaskDetailsSpaceStyles';
import { formatBytes } from '~src/uploadContent/utils/UploadContentDialogHelper';

import { IExecution } from '../serverApi/types/IExecution';
import { ContentRelayPlanDetailsStyles as Styles } from './ContentRelayPlanDetailsStyles';
import { canDownloadExecution, executionKey, ExecutionRowState, executionRowState } from './planExecutionsService';

export const ContentRelayPlanExecutionsGridTestIds = {
  Grid: 'relay-plan-executions-grid',
  Status: 'relay-plan-executions-status-',
  Download: 'relay-plan-executions-download-',
  Expired: 'relay-plan-executions-expired-',
  Rerun: 'relay-plan-executions-rerun-',
};

export interface ContentRelayPlanExecutionsGridProps {
  readonly executions: ReadonlyArray<IExecution>;
  /** Navigates the browser to the execution's download endpoint. */
  readonly onDownload: (execution: IExecution) => void;
  /** Execution keys whose Download was just clicked; the action is disabled and says it is preparing. */
  readonly heldDownloads: { [key: string]: boolean };
  /** Opens the Run dialog so an expired package can be produced again. */
  readonly onRerun: () => void;
}

const GridContext = React.createContext<ContentRelayPlanExecutionsGridProps>(undefined);

function statusLabel(state: ExecutionRowState, execution: IExecution): string {
  switch (state) {
    case 'ready': return Messages.relayPlanExecutionsStatusReady();
    // An expired package still belongs to a run that succeeded; the Actions cell explains the expiry.
    case 'expired': return Messages.relayPlanExecutionsStatusReady();
    case 'failed': return Messages.relayPlanExecutionsStatusFailed();
    case 'cancelled': return Messages.relayPlanExecutionsStatusCancelled();
    default: return execution.state === 'RUNNING'
      ? Messages.relayPlanExecutionsStatusRunning()
      : Messages.relayPlanExecutionsStatusQueued();
  }
}

function statusIcon(state: ExecutionRowState): JSX.Element {
  switch (state) {
    case 'ready':
    case 'expired': return <StatusActiveIcon size={18} alt={''} />;
    case 'failed': return <StatusErrorIcon size={18} alt={''} />;
    case 'inProgress': return <StatusInProgressIcon size={18} alt={''} />;
    // No design-system icon for canceled; a spacer keeps the label aligned with the other rows.
    default: return <span className={Styles.StatusIconSpacer} aria-hidden={true} />;
  }
}

function StatusCell(props: { execution: IExecution }): JSX.Element {
  const execution = props.execution;
  const state = executionRowState(execution);
  return (
    <span
      className={TaskDetailsSpaceStyles.StatusIndicator}
      data-tb-test-id={ContentRelayPlanExecutionsGridTestIds.Status + executionKey(execution)}
    >
      {statusIcon(state)}
      {statusLabel(state, execution)}
    </span>
  );
}

function ActionsCell(props: { execution: IExecution }): JSX.Element {
  const context = React.useContext(GridContext);
  const execution = props.execution;
  const key = executionKey(execution);
  if (executionRowState(execution) === 'expired') {
    return (
      <React.Fragment>
        <span className={Styles.ExpiredNote} title={Messages.relayPlanExecutionsExpired()} data-tb-test-id={ContentRelayPlanExecutionsGridTestIds.Expired + key}>
          {Messages.relayPlanExecutionsExpired()}
        </span>
        <ButtonWidget
          buttonType={ButtonType.LowEmphasis}
          onClick={context.onRerun}
          testId={ContentRelayPlanExecutionsGridTestIds.Rerun + key}
        >
          {Messages.relayPlanExecutionsRerun()}
        </ButtonWidget>
      </React.Fragment>
    );
  }
  if (!canDownloadExecution(execution)) {
    return null;
  }
  const isHeld = !!context.heldDownloads[key];
  return (
    <ButtonWidget
      buttonType={ButtonType.LowEmphasis}
      onClick={function() { context.onDownload(execution); }}
      disabled={isHeld}
      aria-label={Messages.relayPlanExecutionsDownloadLabel({ STARTED: startedText(execution) })}
      testId={ContentRelayPlanExecutionsGridTestIds.Download + key}
    >
      {isHeld ? Messages.relayPlanExecutionsDownloadPreparing() : Messages.relayPlanExecutionsDownload()}
    </ButtonWidget>
  );
}

function durationText(execution: IExecution): string {
  const started = Date.parse(execution.startedAt || execution.createdAt);
  const completed = Date.parse(execution.completedAt);
  if (isNaN(started) || isNaN(completed) || completed < started) {
    return '';
  }
  const duration = new Duration(new Date(started), new Date(completed));
  const pad = function(n: number): string { return n < 10 ? '0' + n : String(n); };
  return pad(duration.hours) + ':' + pad(duration.minutes) + ':' + pad(duration.seconds);
}

function sizeText(execution: IExecution): string {
  const size = execution.bundle && execution.bundle.sizeBytes;
  return size === undefined || size === null ? '' : formatBytes(size);
}

function startedText(execution: IExecution): string {
  const started = execution.startedAt || execution.createdAt;
  return started ? L10n.localizedDateTime(new Date(started)) : '';
}

function cellContent(col: string, execution: IExecution): React.ReactNode {
  switch (col) {
    case 'status': return <StatusCell execution={execution} />;
    case 'started': return startedText(execution);
    case 'duration': return durationText(execution);
    case 'size': return sizeText(execution);
    case 'executor': return execution.executedByName || null;
    case 'download': return <ActionsCell execution={execution} />;
    default: return null;
  }
}

function ExecutionsCell(props: BodyCell.CellFormatterProps): JSX.Element {
  const execution = props.row.data as IExecution;
  return <div className={Styles.Cell}>{cellContent(props.col.name, execution)}</div>;
}

function HeaderCell(props: HeaderColumn.HeaderFormatterProps): JSX.Element {
  return <div className={Styles.HeaderCell}>{props.col.headerText}</div>;
}

function column(name: string, headerText: string, preferredWidth: number): DataGrid.Column {
  return {
    name: name,
    dataKey: name,
    headerText: headerText,
    cellComponent: ExecutionsCell,
    headerComponent: HeaderCell,
    sortable: false,
    preferredWidth: preferredWidth,
  };
}

function buildColumns(): DataGrid.Column[] {
  return [
    column('started', Messages.relayPlanExecutionsStarted(), 220),
    column('duration', Messages.duration(), 140),
    column('status', Messages.status(), 160),
    column('executor', Messages.relayPlanExecutionsExecutor(), 180),
    { ...column('size', Messages.size(), 120), growToFit: true },
    { ...column('download', Messages.actions(), 360), resizable: false },
  ];
}

/** The signed-in user's executions of the plan, newest first (the order the server returns them). */
export function ContentRelayPlanExecutionsGrid(props: ContentRelayPlanExecutionsGridProps): JSX.Element {
  const cols = React.useMemo(buildColumns, []);
  const rowStore = React.useMemo(function() {
    return new ArrayRowStore(props.executions.map(function(execution): DataGrid.Row {
      return { key: executionKey(execution), data: { ...execution } };
    }));
  }, [props.executions]);

  return (
    <GridContext.Provider value={props}>
      <div
        className={classes('tb-spaces-list-view', ExplorerStyles.ListView, ExplorerStyles.DataGridBodyTopBorder, Styles.GridRegion)}
        data-tb-test-id={ContentRelayPlanExecutionsGridTestIds.Grid}
      >
        <DataGrid
          cols={cols}
          rowStore={rowStore}
          config={{
            colsPerChunk: cols.length,
            rowOuterHeight: ExplorerStyles.LIST_VIEW_ROW_OUTER_HEIGHT,
            rememberLastScrollPos: true,
          }}
          showVerticalGridLines={false}
          showHorizontalGridLines={true}
          showRowBanding={false}
          aria-label={Messages.relayPlanExecutionsGridLabel()}
        />
      </div>
    </GridContext.Provider>
  );
}
